import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { ASSETS, SUPPORT_EMAIL } from "@/constants/assets";
import Footer from "@/components/Footer";
import PasswordInput from "@/components/PasswordInput";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Bienvenue ${u.full_name.split(" ")[0]}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const forgot = (e) => {
    e.preventDefault();
    toast.info(
      `Pour réinitialiser votre mot de passe, écrivez à ${SUPPORT_EMAIL} depuis l'adresse liée à votre compte.`,
      { duration: 6000 },
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F9FB]" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center px-4 py-10 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo header */}
          <div className="flex flex-col items-center mb-8">
            <div className="rounded-full bg-white shadow-md border border-[#E4E7EC] p-2">
              <img src={ASSETS.logo} alt="Institut PROSPECT'HEURE" className="h-16 w-16 md:h-20 md:w-20 rounded-full object-contain" />
            </div>
            <div className="text-[11px] text-[#9A762F] tracking-[0.22em] uppercase font-semibold mt-4">
              Institut PROSPECT&rsquo;HEURE
            </div>
          </div>

          <form
            onSubmit={submit}
            className="ph-card p-6 md:p-8 rounded-2xl border border-[#E4E7EC] shadow-sm"
            data-testid="login-form"
          >
            <Link to="/" className="text-[11px] text-[#667085] tracking-[0.22em] uppercase font-semibold hover:text-[#9A762F]">
              ← Retour
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0B1726] mt-4 tracking-tight">
              Bon retour
            </h1>
            <p className="text-[14px] text-[#475467] mt-2">
              Accédez à votre espace membre PROSPECT&rsquo;HEURE.
            </p>

            <div className="mt-6 space-y-4">
              <label className="block">
                <span className="text-[11px] text-[#475467] tracking-wider uppercase font-semibold">Adresse e-mail</span>
                <div className="relative mt-1">
                  <Mail size={16} className="absolute left-3 top-3 text-[#98A2B3]" />
                  <input
                    required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="ph-input w-full pl-10 pr-3 py-2.5 rounded-md text-[14px]"
                    placeholder="vous@email.com"
                    data-testid="login-email"
                  />
                </div>
              </label>

              <PasswordInput
                label="Mot de passe"
                testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={forgot}
                  className="text-[12px] text-[#9A762F] font-semibold hover:underline"
                  data-testid="login-forgot"
                >
                  Mot de passe oublié ?
                </button>
              </div>

              <button
                type="submit" disabled={loading}
                className="gold-btn gold-btn-pill w-full inline-flex items-center justify-center gap-2 py-3 disabled:opacity-60"
                data-testid="login-submit"
              >
                {loading ? "Connexion…" : "Se connecter"} <ArrowRight size={16} />
              </button>

              <div className="text-[13px] text-[#667085] pt-4 text-center border-t border-[#E4E7EC]">
                Pas encore de compte ?{" "}
                <Link to="/register" className="text-[#9A762F] font-semibold hover:underline" data-testid="login-to-register">
                  Créer votre espace membre
                </Link>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
